import React from 'react';
import { Link } from 'react-router-dom';
import OtherArtsSection from '../../components/OtherArtsSection';
import GalleryLightbox from '../../components/GalleryLightbox';
import tours from '../../data/tours';
import TourModal from '../../components/TourModal';

/*
  Images referenced elsewhere — keep this list in sync when you add/remove page-specific images.
*/
const usedImageNames = new Set([
  'RaghuramShetty_Shumbha_HeadshotYakshaganaPhoto_20161229_010652.jpg',
  'Yakshagana_BayareaSiliconValley_Independenceday_RaghuramShetty.jpg',
  'BoothaKola1.jpg',
  'BoothaKola2.jpg',
  'BoothaKolaPerforamnce_WaterMarked_PanjurliWithANi_RaghuramShetty.png',
  'DSC_3704.jpg',
  'RaghuramShettyAsPunjurliBootha_0I7A0194.png',
  'ButaKola_Ritual_BackgroundSetupForProjection_PropertyOfRaghuramShetty_YakshalokaUS.jpg',
  'BhutaSthanaMaada_e2_WaterMarked_BhutaGudi_PhotoByRaghuramShetty.jpg',
  'BootaMogaMoorti_ManemanchaavuGudi_e2_WaterMarked_FacesOfSpirts_ByRaghuramShettyOn01192013_DSC06811.png',
  'PrathibhaShetty_DeviDurgaOnLion_Mahishamardini_Yakshagana_20161229_224345.jpg',
  'hero-background.jpg',
  'Logo.png'
]);

// Recursively import all images from `assets/images` and group those inside
// `assets/images/gallery/<category>` into respective collections.
const importAllRecursive = (r) => r.keys().map((key) => {
  const path = key.replace('./', '');
  const parts = path.split('/');
  const name = parts[parts.length - 1];
  const src = r(key);
  return { name, src, path, title: name.replace(/\.(jpe?g|png|gif|webp|bmp|jpeg|mov)$/i, ''), description: '' };
});

let allImages = [];
try {
  // load recursively from images directory (this will include gallery subfolders)
  allImages = importAllRecursive(require.context('../../assets/images', true, /\.(jpe?g|png|gif|webp|bmp|jpeg|mov)$/));
} catch (e) {
  // If something goes wrong, fall back to empty array
  allImages = [];
}

// images inside gallery subfolders
const galleryImages = allImages.filter(i => i.path.toLowerCase().startsWith('gallery/'));

const yakImages = galleryImages.filter(i => i.path.toLowerCase().includes('gallery/yakshagana') || /yakshagana|vaali|bannadavesha|raghuram|prathibha/i.test(i.name));
const bootaImages = galleryImages.filter(i => i.path.toLowerCase().includes('gallery/bootakola') || /bootha|boota|buta|panjurli|bootakola/i.test(i.name));
const otherImages = galleryImages.filter(i => !yakImages.some(y => y.name === i.name) && !bootaImages.some(b => b.name === i.name) && !usedImageNames.has(i.name));

const mapItems = (arr) => arr.map(img => ({ type: 'image', src: img.src, alt: img.title, caption: img.description || '' }));

const yakItems = mapItems(yakImages);
const bootaItems = mapItems(bootaImages);
const otherItems = mapItems(otherImages);

// Combined items for the main gallery view (keeps previous behavior).
const items = [...yakItems, ...bootaItems, ...otherItems];

const Gallery = () => {
  const [tourOpen, setTourOpen] = React.useState(false);
  const [tourItems, setTourItems] = React.useState([]);
  const [tourStartIndex, setTourStartIndex] = React.useState(0);

  const openTour = (tour) => {
    const source = tour.group === 'yakshagana' ? yakItems : (tour.group === 'bootakola' ? bootaItems : otherItems);
    const matched = [];
    tour.selectors.forEach(sel => {
      const found = source.filter(i => (i.alt || '').toLowerCase().includes(sel.toLowerCase()) || (i.caption || '').toLowerCase().includes(sel.toLowerCase()) || (i.src || '').toLowerCase().includes(sel.toLowerCase()));
      found.forEach(f => { if (!matched.some(m => m.src === f.src)) matched.push(f); });
    });
    const itemsToShow = matched.length ? matched : source;
    setTourItems(itemsToShow);
    setTourStartIndex(0);
    setTourOpen(true);
  };

  return (
    <div className="gallery container mx-auto py-8">
      <h2 className="text-3xl font-bold mb-6 text-center">Gallery</h2>

      {/* Gallery Navigation */}
      <div className="flex flex-wrap justify-center gap-4 mb-8">
        <Link 
          to="/gallery/yakshagana" 
          className="px-6 py-3 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold hover:from-amber-400 hover:to-amber-500 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-amber-500/50"
        >
          Yakshagana Gallery
        </Link>
        <Link 
          to="/gallery/boothakola" 
          className="px-6 py-3 rounded-full bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold hover:from-red-500 hover:to-red-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-red-500/50"
        >
          Bootha Kola Gallery
        </Link>
      </div>

      {/* Curated Tours */}
      <div className="mb-8 container mx-auto px-4">
        <h3 className="text-xl font-semibold mb-4">Curated Tours</h3>
        <div className="flex flex-wrap gap-4">
          {tours.map(t => (
            <button key={t.id} onClick={() => openTour(t)} className="bg-white/5 border border-white/10 rounded-lg p-4 hover:scale-105 transition-transform">
              <div className="font-semibold">{t.title}</div>
              <div className="text-sm text-gray-300">{t.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Grid + lightbox */}
      <GalleryLightbox items={items} />

      <OtherArtsSection
        title="Gallery Videos"
        subtitle="Curated clips that complement the photo gallery."
        listId="gallery"
        initialViewMode="masonry"
        initialHeaderVariant="voices"
      />
    </div>
  );
};

export default Gallery;
